import type { LaunchTile_launchTileBehavior$key } from "./__generated__/LaunchTile_launchTileBehavior.types.graphql";
import type { LaunchTile_m365App$key } from "./__generated__/LaunchTile_m365App.types.graphql";
import { ShortcutHandler } from "./AppBar.types";
import { BackgroundShadeStyle } from "./BackgroundShadeStyle";
import { graphql } from "./taggedNode";
import { fragment as TileBadge_badge } from "./TileBadge.types";
import {
  modelFragment as TileContextMenu_tileContextMenu,
  TileContextMenuCallbacks,
} from "./TileContextMenu.types";
export type PinStateChangeHandler = (pinned: boolean) => void;
export interface CustomTileAction {
  id: string;
  text: string;
  eventType: string;
  eventData: string;
}
/**
 *
 * @interface LaunchTileProps
 */
export interface LaunchTileProps {
  index: number;
  /**
   * Current view mode of appbar
   * @type {boolean}
   * @memberof LaunchTileProps
   */
  viewMode: string;
  /**
   * The control model fragment describing the launch tile's behavior.
   *
   * @type {LaunchTile_launchTileBehavior$key}
   * @memberof LaunchTileProps
   */
  launchTileBehavior: LaunchTile_launchTileBehavior$key;
  /**
   * The underlying data for the app that the tile represents.
   *
   * @type {LaunchTile_m365App$key}
   * @memberof LaunchTileProps
   */
  m365App: LaunchTile_m365App$key; // Generated opaque key for the generated typing for the LaunchTile_m365App fragment
  /**
   * Registers a shortcut handler for a launch tile
   *
   * @param index
   * @param handler
   */
  assignShortcutHandler?(index: number, handler: ShortcutHandler): void;
  /**
   * Unregisters the shortcut handler for a launch tile
   * @param index
   * @param handler
   */
  unassignShortcutHandler?(index: number, handler: ShortcutHandler): void;
  /**
   * A callback to be executed upon a pin state change.
   *
   * @memberof LaunchTileProps
   */
  onPinStateChange?: PinStateChangeHandler;
  /**
   * Callback executed after tile is clicked
   */
  onClicked?(): void;
  /**
   * Callback executed after a tile is clicked
   */
  onContextMenu?(
    target: HTMLElement,
    pinnedIndex: number | undefined,
    menuCallbacks: TileContextMenuCallbacks,
    targetAppTileId: string
  ): void;
  /**
   * Indicates the linked entity that should be attached to commands as they are triggered.
   *
   * @type {unknown}
   * @memberof LaunchTileProps
   */
  linkedEntity?: unknown;
  /**
   * Indicates whether Mica View 2 Desktop Styles will be used for the Launch Tiles.
   */
  enableMicaV2DesktopStyles?: boolean;
  /**
   * Indicates whether Mica View 2 Web Styles will be used for the Launch Tiles.
   */
  enableMicaV2WebStyles?: boolean;
  /**
   * Indicates whether MercuryDesign styles will be used for the Launch Tiles.
   */
  enableMercuryDesignStyles?: boolean;
  /**
   * Indicates whether drag and drop is enabled
   */
  dragDropEnabled?: boolean;
  /**
   * The background shade CSS class that should be applied to the tile.
   */
  backgroundShade?: BackgroundShadeStyle;
  /**
   * Determines if the Launch Tile should render with role=listitem
   */
  isListItem?: boolean;
  /**
   * Indicates whether Visual Refresh will be used for the Launch Tiles.
   */
  enableVisualRefreshStyles?: boolean;
}
export interface SeeAllTileProps {
  label: string;
  index: number;
  enableMicaV2DesktopStyles?: boolean;
  enableMicaV2WebStyles?: boolean;
}
export const behaviorFragment = graphql`
  fragment LaunchTile_launchTileBehavior on LaunchTileBehavior {
    transparent
    pinningEnabled
    appTilePositionEnabled
    useHubColorForSelectedTileLabel
    useOutlineIcon
    useSmallColorIcon
    platformAppCommands {
      aboutCommandEnabled
      uninstallCommandEnabled
    }
    tileContextMenu {
      ...TileContextMenu_tileContextMenu
    }
  }
  ${TileContextMenu_tileContextMenu}
`;
// The GraphQL fragment representing the LaunchTile data
export const dataFragment = graphql`
  fragment LaunchTile_m365App on M365Application {
    id
    platformType
    name
    accessibleName
    icon {
      __typename
      ... on InternalIcon {
        name
      }
      ... on RemoteImage {
        src
        isFullBleed
      }
    }
    outlineIcon {
      __typename
      ... on InternalIcon {
        name
      }
      ... on RemoteImage {
        src
        isFullBleed
      }
    }
    colorIconSmall {
      __typename
      ... on InternalIcon {
        name
      }
      ... on RemoteImage {
        src
        isFullBleed
      }
    }
    badge {
      __typename
      ... on M365ApplicationNumericBadge {
        count
      }
      ...TileBadge_badge
    }
    selectedStateIconOverride {
      __typename
      ... on InternalIcon {
        name
      }
      ... on RemoteImage {
        src
        isFullBleed
      }
    }
    brandingColor
    isInUse
    canLaunchOut
    enablePlatformCommands
    isPinnable
    lockedIndex
    pinnedIndex
    secondaryActions {
      id
      text
      icon {
        __typename
        ... on InternalIcon {
          name
        }
      }
      eventType
      eventData
    }
    ... on M365PlatformApplication {
      canBeUnacquired
      hasPermissions
    }
    allowedExternalDropContent
  }
  ${TileBadge_badge}
`;
